import { useQuery, useQueryClient } from '@tanstack/react-query';
import sessionsService from '@/lib/api/sessions';

export function useSessionsList(
  organizationId: string | undefined,
  options?: {
    search?: string;
    limit?: number;
    offset?: number;
    qualification_status?: string;
    booked_status?: string;
    session_status?: string;
    source_environment?: string;
    date_from?: string;
    date_to?: string;
    target_session_id?: string;
    enabled?: boolean;
  }
) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [
      'organizationSessions',
      organizationId,
      options?.search,
      options?.limit,
      options?.offset,
      options?.qualification_status,
      options?.booked_status,
      options?.session_status,
      options?.source_environment,
      options?.date_from,
      options?.date_to,
      options?.target_session_id,
    ],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID required');

      const response = await sessionsService.getOrganizationSessions(organizationId, {
        search: options?.search,
        limit: options?.limit,
        offset: options?.offset,
        qualification_status: options?.qualification_status,
        booked_status: options?.booked_status,
        session_status: options?.session_status,
        source_environment: options?.source_environment,
        date_from: options?.date_from,
        date_to: options?.date_to,
        target_session_id: options?.target_session_id,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch sessions');
      }

      return response.data;
    },
    enabled: !!organizationId && (options?.enabled ?? true),
    staleTime: 30 * 1000, // Consider fresh for 30 seconds
  });

  // Prefetch function for hover - now uses sales analytics endpoint
  const prefetchSessionDetail = (sessionId: string) => {
    if (!organizationId) return;

    queryClient.prefetchQuery({
      queryKey: ['salesAnalytics', sessionId],
      queryFn: async () => {
        const response = await sessionsService.getSalesAnalytics(sessionId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to fetch session analytics');
        }
        return response.data;
      },
      staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    });
  };

  return {
    sessions: query.data?.sessions || [],
    total: query.data?.total || 0,
    offset: query.data?.offset ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    prefetchSessionDetail,
  };
}
